import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, type PaginationParams } from '@/types/pagination';

interface UsePaginationOptions {
  defaultPageSize?: number;
}

/**
 * Shared page / rows-per-page state for paged lists. Changing the page size
 * always returns to the first page.
 */
export function usePagination(options: UsePaginationOptions = {}) {
  const { defaultPageSize = DEFAULT_PAGE_SIZE } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setCurrentPageSize] = useState(defaultPageSize);

  const setPage = useCallback((page: number | ((prev: number) => number)) => {
    setCurrentPage((prev) => (typeof page === 'function' ? page(prev) : page));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(1);
  }, []);

  const resetPagination = useCallback(() => setCurrentPage(1), []);

  const paginationParams: PaginationParams = useMemo(
    () => ({ page: currentPage, pageSize }),
    [currentPage, pageSize]
  );

  return {
    currentPage,
    pageSize,
    paginationParams,
    setPage,
    setPageSize,
    resetPagination,
  };
}
